import type {
  Encounter,
  Observation,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { compact } from 'lodash-es'
import { z } from 'zod'
import { ELATION_VISIT_NOTE_VITAL_REFERENCE_SYSTEM } from '#src/codeSystems.ts'
import { elationSandboxIdentifier } from '#src/elation.ts'
import * as LOINC from '#src/models/loinc.ts'
import { ExtendedObservation } from '#src/models/observation.ts'

function parseQuantity(
  value: string,
  units: string
): Observation['valueQuantity'] | undefined {
  const parsed = parseFloat(value)
  if (Number.isNaN(parsed)) {
    return undefined
  }
  return {
    value: parsed,
    unit: units,
    system: 'http://unitsofmeasure.org',
    code: units,
  }
}

const measurementSchema = z.object({
  value: z.string().max(10),
  units: z.string(),
  note: z.string().max(250),
})

const bloodPressureSchema = z.object({
  systolic: z.string().max(10),
  diastolic: z.string().max(10),
  note: z.string().max(250),
})

const painSchema = z.object({
  value: z.string(),
  note: z.string(),
})

export const vitalSchema = z.object({
  id: z.number().int().positive(),
  bmi: z.number(),
  height: z.array(measurementSchema).length(1),
  weight: z.array(measurementSchema).length(1),
  oxygen: z.array(measurementSchema).length(1),
  rr: z.array(measurementSchema).length(1),
  hr: z.array(measurementSchema).length(1),
  hc: z.array(measurementSchema).length(1),
  temperature: z.array(measurementSchema).length(1),
  bp: z.array(bloodPressureSchema),
  pain: z.array(painSchema),
  bmi_percentile: z.string(),
  length_for_weight_percentile: z.string(),
  patient: z.number().int().positive(),
  practice: z.number().int().positive(),
  visit_note: z.number().int().positive(),
  non_visit_note: z.number().int().nullable(),
  document_date: z.string().datetime(),
  chart_date: z.string().datetime(),
  signed_date: z.string().datetime().nullable(),
  signed_by: z.number().int().positive().nullable(),
  created_date: z.string().datetime(),
  deleted_date: z.string().datetime().nullable(),
})

export type Vital = z.infer<typeof vitalSchema>

export const fromFhir = (_observations: Observation[]): Vital => {
  throw new Error('Not implemented')
}

export const toFhir = ({
  vital,
  performer,
  subject,
  encounter,
}: {
  vital: Vital
  performer: Reference<Practitioner>
  subject: Reference<Patient>
  encounter?: Reference<Encounter>
}): Observation[] => {
  const observations: Observation[] = []

  const sharedObservationFields = {
    status: 'final',
    subject,
    performer: [performer],
    effectiveDateTime: vital.chart_date ?? vital.created_date,
    identifier: [elationSandboxIdentifier(vital.id.toString())],
    ...(encounter && { encounter }),
  } satisfies Partial<Observation>

  vital.height.forEach((height, idx) => {
    if (height.value && height.units) {
      const observation = new ExtendedObservation({
        ...sharedObservationFields,
        code: LOINC.BODY_HEIGHT_CODING,
        valueQuantity: parseQuantity(height.value, height.units),
        note: height.note ? [{ text: height.note }] : undefined,
      })
      observation.addTag({
        system: ELATION_VISIT_NOTE_VITAL_REFERENCE_SYSTEM,
        code: `${vital.id}-height-${idx}`,
      })
      observations.push(observation.toFhirResource())
    }
  })

  vital.weight.forEach((weight, idx) => {
    if (weight.value && weight.units) {
      const observation = new ExtendedObservation({
        ...sharedObservationFields,
        code: LOINC.BODY_WEIGHT_CODING,
        valueQuantity: parseQuantity(weight.value, weight.units),
        note: weight.note ? [{ text: weight.note }] : undefined,
      })
      observation.addTag({
        system: ELATION_VISIT_NOTE_VITAL_REFERENCE_SYSTEM,
        code: `${vital.id}-weight-${idx}`,
      })
      observations.push(observation.toFhirResource())
    }
  })

  vital.oxygen.forEach((oxygen, idx) => {
    if (oxygen.value && oxygen.units) {
      const observation = new ExtendedObservation({
        ...sharedObservationFields,
        code: LOINC.OXYGEN_SATURATION_CODING,
        valueQuantity: parseQuantity(oxygen.value, oxygen.units),
        note: oxygen.note ? [{ text: oxygen.note }] : undefined,
      })
      observation.addTag({
        system: ELATION_VISIT_NOTE_VITAL_REFERENCE_SYSTEM,
        code: `${vital.id}-oxygen-${idx}`,
      })
      observations.push(observation.toFhirResource())
    }
  })

  vital.rr.forEach((rr, idx) => {
    if (rr.value && rr.units) {
      const observation = new ExtendedObservation({
        ...sharedObservationFields,
        code: LOINC.RESPIRATORY_RATE_CODING,
        valueQuantity: parseQuantity(rr.value, rr.units),
        note: rr.note ? [{ text: rr.note }] : undefined,
      })
      observation.addTag({
        system: ELATION_VISIT_NOTE_VITAL_REFERENCE_SYSTEM,
        code: `${vital.id}-rr-${idx}`,
      })
      observations.push(observation.toFhirResource())
    }
  })

  vital.hr.forEach((hr, idx) => {
    if (hr.value && hr.units) {
      const observation = new ExtendedObservation({
        ...sharedObservationFields,
        code: LOINC.HEART_RATE_CODING,
        valueQuantity: parseQuantity(hr.value, hr.units),
        note: hr.note ? [{ text: hr.note }] : undefined,
      })
      observation.addTag({
        system: ELATION_VISIT_NOTE_VITAL_REFERENCE_SYSTEM,
        code: `${vital.id}-hr-${idx}`,
      })
      observations.push(observation.toFhirResource())
    }
  })

  vital.hc.forEach((hc, idx) => {
    if (hc.value && hc.units) {
      const observation = new ExtendedObservation({
        ...sharedObservationFields,
        code: LOINC.HEAD_CIRCUMFERENCE_CODING,
        valueQuantity: parseQuantity(hc.value, hc.units),
        note: hc.note ? [{ text: hc.note }] : undefined,
      })
      observation.addTag({
        system: ELATION_VISIT_NOTE_VITAL_REFERENCE_SYSTEM,
        code: `${vital.id}-hc-${idx}`,
      })
      observations.push(observation.toFhirResource())
    }
  })

  vital.temperature.forEach((temperature, idx) => {
    if (temperature.value && temperature.units) {
      const observation = new ExtendedObservation({
        ...sharedObservationFields,
        code: LOINC.BODY_TEMPERATURE_CODING,
        valueQuantity: parseQuantity(temperature.value, temperature.units),
        note: temperature.note ? [{ text: temperature.note }] : undefined,
      })
      observation.addTag({
        system: ELATION_VISIT_NOTE_VITAL_REFERENCE_SYSTEM,
        code: `${vital.id}-temperature-${idx}`,
      })
      observations.push(observation.toFhirResource())
    }
  })

  vital.bp.forEach((bp, idx) => {
    if (bp.systolic || bp.diastolic) {
      const observation = new ExtendedObservation({
        ...sharedObservationFields,
        code: LOINC.BLOOD_PRESSURE_CODING,
        component: compact([
          bp.systolic && {
            code: LOINC.SYSTOLIC_BLOOD_PRESSURE_CODING,
            valueString: bp.systolic,
          },
          bp.diastolic && {
            code: LOINC.DIASTOLIC_BLOOD_PRESSURE_CODING,
            valueString: bp.diastolic,
          },
        ]),
        note: bp.note ? [{ text: bp.note }] : undefined,
      })
      observation.addTag({
        system: ELATION_VISIT_NOTE_VITAL_REFERENCE_SYSTEM,
        code: `${vital.id}-bp-${idx}`,
      })
      observations.push(observation.toFhirResource())
    }
  })

  vital.pain.forEach((pain, idx) => {
    if (pain.value) {
      const observation = new ExtendedObservation({
        ...sharedObservationFields,
        code: LOINC.PAIN_SEVERITY_CODING,
        valueString: pain.value,
        note: pain.note ? [{ text: pain.note }] : undefined,
      })
      observation.addTag({
        system: ELATION_VISIT_NOTE_VITAL_REFERENCE_SYSTEM,
        code: `${vital.id}-pain-${idx}`,
      })
      observations.push(observation.toFhirResource())
    }
  })

  if (vital.bmi) {
    const observation = new ExtendedObservation({
      ...sharedObservationFields,
      code: LOINC.BODY_MASS_INDEX_CODING,
      valueString: vital.bmi.toString(),
    })
    observation.addTag({
      system: ELATION_VISIT_NOTE_VITAL_REFERENCE_SYSTEM,
      code: `${vital.id}-bmi`,
    })
    observations.push(observation.toFhirResource())
  }

  if (vital.bmi_percentile) {
    const observation = new ExtendedObservation({
      ...sharedObservationFields,
      code: LOINC.BODY_MASS_INDEX_PERCENTILE_CODING,
      valueString: vital.bmi_percentile,
    })
    observation.addTag({
      system: ELATION_VISIT_NOTE_VITAL_REFERENCE_SYSTEM,
      code: `${vital.id}-bmi-percentile`,
    })
    observations.push(observation.toFhirResource())
  }

  return observations
}
